import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import { useDirectBackend, useBugReporter } from "#imports";
import type { ItemSchedule } from "~/domain/item-schedule";
import type { IDomainPaginatedList } from "~/domain/paginated-list";
import { useBusinessStore } from "~/stores/business";

type IState = {
  schedulesByOutlet: Record<string, ItemSchedule[]>;
};

function createInitialState() {
  return {
    schedulesByOutlet: {},
  } satisfies IState;
}

export const useItemSchedulesStore = defineStore("itemSchedules", () => {
  const { menuRepo } = useDirectBackend();
  const { report } = useBugReporter();
  const business = useBusinessStore();

  const state = reactive<IState>(createInitialState());

  function reset() {
    Object.assign(state, createInitialState());
  }

  /**
   * Load all item schedules for a specific outlet.
   * Results are cached, so subsequent calls for the same outlet return cached data.
   *
   * @param outletId - The outlet identifier
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   * @returns Array of item schedules
   */
  const loadSchedules = async (
    outletId: string,
    forceRefresh = false,
  ): Promise<ItemSchedule[]> => {
    if (!forceRefresh && state.schedulesByOutlet[outletId])
      return state.schedulesByOutlet[outletId];

    try {
      const response: IDomainPaginatedList<ItemSchedule> =
        await menuRepo.getItemSchedules({
          outletId,
          salesChannel: business.salesChannel,
        });

      state.schedulesByOutlet[outletId] = response.data;
      return response.data;
    } catch (error) {
      report(error);
      state.schedulesByOutlet[outletId] = [];
      return [];
    }
  };

  /**
   * Computed property to check if schedules are loaded for a given outlet.
   */
  const isLoaded = computed(
    () => (outletId: string) => !!state.schedulesByOutlet[outletId],
  );

  return {
    ...toRefs(state),
    isLoaded,
    loadSchedules,
    reset,
  };
});
